<script setup lang="ts">
  /**
   * Table pager, drawn as the design system's Pagination block (03-components/12b-data-table).
   *
   * The pager draws no chrome of its own on desktop — only the count, the controls, and the
   * card it becomes on a narrow viewport. Both placements the app uses supply their own:
   * mounted in `BaseTable`'s `pager` slot it sits inside the table frame, which gives it the
   * top hairline and the frame's cell padding (the design system's own placement); mounted in a
   * page's footer row under the table it sits flush, and the page owns the spacing.
   *
   * `pageSizeOptions` turns on the design system's rows-per-page control as the pager's leading
   * child. It is optional: omit it where the row count never changes or the page size is fixed
   * by the API, and the pager lays out exactly as it did before.
   *
   * `aria-current="page"` — not a class — is what the active style hangs off, so the markup and
   * the accessible state cannot drift apart.
   *
   * The root is the labelled landmark, so the button row needs no `role="group"` of its own:
   * the design system puts one there only because its own root is an unlabelled `<div>`.
   * Consumers pass the label (`aria-label="Invoice table pagination"`).
   */
  import { computed, PropType, ref, useId } from 'vue'

  import BfPaginationPageSize from '@/components/ui/pagination/BfPaginationPageSize.vue'
  import { BfPageSizeOption } from '@/typescript/types/ui-components/BfPaginationI'

  const pageLimit = 5

  const props = defineProps({
    /** Zero-based page index. */
    modelValue: {
      type: Number,
      default: 1,
    },
    /** Number of pages. */
    total: {
      type: Number,
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Rows across every page. Passed, the count reads the design system's full sentence —
     * "Showing 1-10 of 47 invoices · Page 1 of 5"; omitted, it stays "Page 1 of 5".
     */
    totalItems: {
      type: Number,
      default: undefined,
    },
    /** Rows per page. Only read alongside `totalItems`, to work out the range. */
    pageSize: {
      type: Number,
      default: 0,
    },
    /** What the rows are, for the count sentence. */
    itemNoun: {
      type: String,
      default: 'rows',
    },
    /**
     * Page sizes to offer, as `{ label, value }`. Passed, the pager grows the design system's
     * rows-per-page control ahead of the count; omitted, there is no control and no label.
     */
    pageSizeOptions: {
      type: Array as PropType<BfPageSizeOption[]>,
      default: () => [],
    },
    /** Names the rows-per-page control. It has no `<label for>` — this is its accessible name. */
    pageSizeLabel: {
      type: String,
      default: 'Rows per page',
    },
    /**
     * Mounted in `BaseTable`'s `pager` slot, i.e. inside the table frame. Takes the frame's
     * cell padding and a top hairline, so the pager reads as the table's last row.
     */
    docked: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits(['update:modelValue', 'update:pageSize'])

  /** The control is a listbox, not a form field, so it is named by this id rather than a label. */
  const pageSizeLabelId = useId()

  const hasPageSize = computed(() => props.pageSizeOptions.length > 0)

  const currentPage = computed({
    get() {
      return props.modelValue
    },
    set(newValue) {
      emit('update:modelValue', newValue)
    },
  })

  /** 1-based, for everything the customer reads. */
  const humanPage = computed(() => props.modelValue + 1)

  const countText = computed(() => {
    const pageLine = `Page ${humanPage.value} of ${props.total}`
    if (props.totalItems === undefined) return pageLine
    if (!props.totalItems) return `0 ${props.itemNoun}`
    // A pager driven by "show all entries" has no page size of its own to divide by.
    const size = props.pageSize || props.totalItems
    const from = props.modelValue * size + 1
    const to = Math.min(humanPage.value * size, props.totalItems)
    return `Showing ${from}-${to} of ${props.totalItems} ${props.itemNoun} · ${pageLine}`
  })

  const root = ref<HTMLElement | null>(null)

  /**
   * A new page is a new list: bring the customer back to its start rather than leaving them at
   * the pager, below the fold. Only on a change the customer made here — a page or a page size
   * (consumers restart at the first page, so 25 → 100 is a new list too). A consumer resetting
   * `modelValue` is not a navigation.
   *
   * The window is not what scrolls in the console: DefaultLayout pins the shell to the viewport
   * and its content column (`.layout-content`) is the scroller. So every vertically scrolling
   * ancestor is reset, and the window too for a page mounted outside that layout.
   */
  function scrollToTop() {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const options = { top: 0, behavior: prefersReducedMotion ? ('auto' as const) : ('smooth' as const) }

    for (let el = root.value?.parentElement; el; el = el.parentElement) {
      const { overflowY } = getComputedStyle(el)
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollTop > 0) el.scrollTo?.(options)
    }
    if (window.scrollY > 0) window.scrollTo(options)
  }

  function changePage(number: number) {
    currentPage.value = number
    scrollToTop()
  }

  function changePageSize(size: string | number) {
    emit('update:pageSize', size)
    scrollToTop()
  }

  function goPrevPage() {
    changePage(currentPage.value - 1)
  }

  function goNextPage() {
    changePage(currentPage.value + 1)
  }

  function goToPage(number: number) {
    changePage(number)
  }

  function isPageVisible(number: number) {
    if (number === 1 || number === props.total) return true
    const currentPageNumber = currentPage.value + 1
    if (currentPageNumber >= pageLimit) {
      // End numbers case
      if (currentPageNumber > props.total - pageLimit + 1) {
        return number > props.total - pageLimit
      }
      // Middle numbers case
      return number === currentPageNumber + 1 || number === currentPageNumber || number === currentPageNumber - 1
    } else {
      // First numbers case
      return number <= pageLimit
    }
  }

  function isDotsVisible(number: number) {
    const currentPageNumber = currentPage.value + 1
    if (currentPageNumber >= pageLimit) {
      return number === 2 || number === props.total - 1
    } else {
      // After 1st nth pages before the last page
      return currentPage.value < pageLimit - 1 && number === pageLimit + 1 && number !== props.total
    }
  }
</script>

<template>
  <nav ref="root" class="pagination" :class="{ 'pagination--docked': docked }">
    <div v-if="hasPageSize" class="pagination__page-size">
      <span :id="pageSizeLabelId" class="pagination__page-size-label">{{ pageSizeLabel }}</span>
      <BfPaginationPageSize
        :model-value="pageSize"
        :options="pageSizeOptions"
        :labelled-by="pageSizeLabelId"
        :disabled="disabled"
        @update:model-value="changePageSize"
      />
    </div>

    <span class="pagination__count" aria-live="polite" aria-atomic="true">{{ countText }}</span>
    <div class="pagination__navigation">
      <button
        type="button"
        class="pagination__button pagination__button--prev"
        :disabled="disabled || currentPage === 0"
        :aria-label="currentPage === 0 ? 'Previous page (unavailable)' : 'Go to previous page'"
        @click="goPrevPage"
      >
        <BfIconChevronRight />
      </button>

      <template v-for="item in total" :key="`bf-page-btn-${item}`">
        <button
          v-if="isPageVisible(item)"
          type="button"
          class="pagination__button pagination__button--page"
          :disabled="disabled || item === humanPage"
          :aria-label="`Go to page ${item}`"
          :aria-current="item === humanPage ? 'page' : undefined"
          @click="goToPage(item - 1)"
        >
          {{ item }}
        </button>
        <span v-else-if="isDotsVisible(item)" class="pagination__ellipsis" aria-hidden="true">…</span>
      </template>

      <button
        type="button"
        class="pagination__button pagination__button--next"
        :disabled="disabled || currentPage === total - 1"
        :aria-label="currentPage === total - 1 ? 'Next page (unavailable)' : 'Go to next page'"
        @click="goNextPage"
      >
        <BfIconChevronRight />
      </button>
    </div>
  </nav>
</template>

<style scoped lang="scss">
  /* No surface and no padding of its own: whichever container the pager is mounted in — the
     table frame or a page's footer row — owns the spacing around it. The narrow-viewport block
     below is where it becomes a card. */
  .pagination {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  /* Docked in `BaseTable`'s frame. The padding is the frame's own cell density, inherited down
     as a custom property, so pager density and row density cannot drift apart; the hairline is
     the rule that would have sat under the last row. */
  .pagination--docked {
    padding: var(--bf-data-table-padding-vertical) var(--bf-data-table-padding-horizontal);
    border-top: 1px solid var(--bf-line);
    background: var(--bf-white);
  }

  .pagination__count {
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-2);
    font-variant-numeric: tabular-nums;
  }

  /* The navigation takes the right edge with `margin-left: auto` rather than the row using
     `justify-content: space-between`, so the optional page-size block inserts ahead of the
     count without a wrapper and a pager without one lays out exactly as before. */
  .pagination__navigation {
    display: flex;
    align-items: center;
    gap: var(--bf-sp-1);
    margin-left: auto;
  }

  /* 8px, like the row's own 14px, is off the spacing scale — the design system declares both
     literally here rather than rounding the pager onto it. */
  .pagination__page-size {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pagination__page-size-label {
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-2);
    white-space: nowrap;
  }

  .pagination__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 32px;
    height: 32px;
    padding: 0 9px;
    border: 1px solid transparent;
    border-radius: var(--bf-r-md);
    background: transparent;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-semibold);
    letter-spacing: 0.01em;
    color: var(--bf-fg-1);
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    transition:
      background-color var(--bf-dur-fast) var(--bf-ease),
      color var(--bf-dur-fast) var(--bf-ease),
      border-color var(--bf-dur-fast) var(--bf-ease);
  }

  .pagination__button:hover:not(:disabled):not([aria-current='page']) {
    background: var(--bf-blue-light);
    color: var(--bf-orange-dark);
  }

  .pagination__button:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: 1px;
  }

  .pagination__button:disabled {
    color: var(--bf-fg-3);
    cursor: not-allowed;
  }

  /* Current page: the same orange-on-tint pairing as the active tab, plus a border so it
     survives at 13px on a white row. Declared after `:disabled` on purpose — the button for
     the page you are already on is disabled, and must still read as the current one. */
  .pagination__button[aria-current='page'] {
    background: var(--bf-nav-active);
    border-color: var(--bf-orange-dark);
    color: var(--bf-orange-dark);
    cursor: default;
  }

  .pagination__button svg {
    width: 12px;
    height: 12px;
    flex: 0 0 auto;
  }

  /* One chevron glyph serves both directions. */
  .pagination__button--prev svg {
    transform: rotate(180deg);
  }

  /* Ellipsis is text, never a button — nothing to focus, nothing to announce. */
  .pagination__ellipsis {
    min-width: 20px;
    text-align: center;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-3);
    user-select: none;
  }

  /* --bf-bp-md: the table frame dissolves into cards, so the pager becomes its own card and
     keeps only the two ends of the range — the numbered pages have no room.

     ONE row: prev / count / next (DS 1.1.94). `__navigation` goes `display: contents` so its two
     surviving buttons become the row's own flex items and the count can sit BETWEEN them; the
     row's `align-items: center` then keeps both arrows on the text's vertical middle however
     many lines it wraps to. The count shrinks (`flex: 1 1 0`, `min-width: 0`) rather than
     wrapping onto a second row — which is what the two `flex: 1 0 100%` declarations this
     replaces did, laying the pager out on two lines. Both buttons carry their own `aria-label`
     and the root is a labelled `<nav>`, so the group box going boxless loses nothing. */
  @mixin pagination-narrow {
    .pagination {
      margin-top: 14px;
      padding: 14px 18px;
      border: 1px solid var(--bf-line);
      border-radius: var(--bf-r-lg);
      background: var(--bf-white);
      box-shadow: var(--bf-shadow-1);
    }

    .pagination__navigation {
      display: contents;
    }

    .pagination__button--prev {
      order: 1;
    }

    .pagination__count {
      order: 2;
      flex: 1 1 0;
      min-width: 0;
      text-align: center;
    }

    .pagination__button--next {
      order: 3;
    }

    /* Dropped at the same breakpoint as the numbered pages: a narrow pager is prev/next/count
       only, and a control that changes how many rows a card shows has nothing to act on. */
    .pagination__page-size,
    .pagination__button--page,
    .pagination__ellipsis {
      display: none;
    }
  }

  /* Standalone (undocked) the pager has no container to ask, so the viewport decides. */
  @media (max-width: 700px) {
    @include pagination-narrow;
  }

  /* Docked, the pager sits inside BaseTable's container, and the table folds on its OWN width
     (12b). Without this the rows would become cards while the pager stayed wide — a narrow table
     in a wide window. Both queries can match at once; they set the same values. */
  @container (max-width: 700px) {
    @include pagination-narrow;
  }
</style>
